import { Component, Input, Output, EventEmitter, OnChanges, SimpleChanges, DestroyRef, inject } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Dialog } from 'primeng/dialog';
import { InputText } from 'primeng/inputtext';
import { Select } from 'primeng/select';
import { DatePicker } from 'primeng/datepicker';
import { MultiSelect } from 'primeng/multiselect';
import { Checkbox } from 'primeng/checkbox';
import { Button } from 'primeng/button';
import { Tooltip } from 'primeng/tooltip';
import { MessageService } from 'primeng/api';
import { forkJoin, catchError, of } from 'rxjs';
import { EmployeeService } from '../../services/employee.service';
import { RoleService } from '../../services/role.service';
import { DesignationService } from '../../services/designation.service';
import { CategoryService } from '../../services/category.service';
import { ServiceGroupService } from '../../services/service-group.service';
import { OnboardingService } from '../../services/onboarding.service';
import { Employee, ENTRA_ID_GROUP_OPTIONS } from '../../models/employee.model';

export interface EmployeeSavedEvent {
  employee: Employee;
  isNew: boolean;
  /**
   * Whether the caller should create an onboarding checklist for this new employee. False for
   * staff who joined before this system existed, or were onboarded offline — they need an
   * employee record without a checklist nobody will ever complete.
   */
  startOnboarding?: boolean;
}

interface DropdownOption {
  label: string;
  value: string;
}

@Component({
  selector: 'app-employee-form-dialog',
  standalone: true,
  imports: [CommonModule, FormsModule, Dialog, InputText, Select, MultiSelect, DatePicker, Checkbox, Button, Tooltip],
  templateUrl: './employee-form-dialog.component.html',
  styleUrl: './employee-form-dialog.component.scss'
})
export class EmployeeFormDialogComponent implements OnChanges {
  private destroyRef = inject(DestroyRef);

  /** Pass null to open in Add mode; pass an Employee to open in Edit mode. */
  @Input() employee: Employee | null = null;
  @Input() visible = false;

  /**
   * Default state of the "start onboarding checklist" checkbox for a NEW employee.
   * <p>
   * The two hosts of this dialog want opposite defaults: adding from the Onboarding module is
   * normally a new joiner who needs a checklist, while adding from Employee Management is usually
   * back-filling existing staff. Both show the same control, so the behaviour is visible and
   * changeable either way instead of differing silently between screens.
   * </p>
   */
  @Input() startOnboardingByDefault = false;

  /**
   * In edit mode: whether this employee already has an onboarding record.
   * <p>
   * When true the checkbox is shown ticked and disabled — onboarding cannot be started twice, and
   * unticking must never delete a record whose tasks may already be part-complete. When false the
   * checkbox is offered so someone who forgot to tick it at creation can still start onboarding.
   * </p>
   */
  @Input() hasOnboardingRecord = false;

  /** Bound to that checkbox; emitted back so the caller knows whether to start onboarding. */
  startOnboarding = false;
  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() saved = new EventEmitter<EmployeeSavedEvent>();
  @Output() cancel = new EventEmitter<void>();

  form: Employee = this.getEmpty();

  // Employee.joiningDate is a 'YYYY-MM-DD' string, but p-datepicker binds a Date.
  // Keep a Date alongside the form and convert on load/save so the API contract
  // is unchanged. (Previously this field was a native <input type="date">, which
  // rendered the browser's own calendar instead of the app's.)
  joiningDatePicker: Date | null = null;

  /** Same string<->Date bridge as joiningDatePicker, for the inactive date. */
  inactiveDatePicker: Date | null = null;
  isEditMode = false;
  fieldTouched: Record<string, boolean> = {};
  cvFile: File | null = null;
  cvUploading = false;
  saving = false;
  loadingOptions = false;
  optionsLoaded = false;

  roleOptions: DropdownOption[] = [];
  designationOptions: DropdownOption[] = [];
  categoryOptions: DropdownOption[] = [];
  serviceGroupOptions: DropdownOption[] = [];
  headOfDepartmentOptions: DropdownOption[] = [];
  employmentTypeOptions = [
    { label: 'Permanent', value: 'Permanent' },
    { label: 'Contract', value: 'Contract' },
    { label: 'Intern', value: 'Intern' }
  ];
  entraIdGroupOptions = ENTRA_ID_GROUP_OPTIONS;

  constructor(
    private employeeService: EmployeeService,
    private roleService: RoleService,
    private designationService: DesignationService,
    private categoryService: CategoryService,
    private serviceGroupService: ServiceGroupService,
    private onboardingService: OnboardingService,
    private messageService: MessageService
  ) {}

  ngOnChanges(changes: SimpleChanges): void {
    // The default can arrive in the same change pass as `visible`, or in a later one if the host
    // binds it after opening — apply it whenever it changes so the checkbox is never left showing
    // the wrong state for this host.
    if (changes['startOnboardingByDefault'] && !this.isEditMode) {
      this.startOnboarding = this.startOnboardingByDefault;
    }

    if (changes['visible']?.currentValue === true) {
      this.isEditMode = !!this.employee;
      // Editing: reflect reality — ticked when a record exists (and disabled), otherwise unticked
      // and available so a missed checklist can still be started. Adding: this host's default.
      this.startOnboarding = this.isEditMode
        ? this.hasOnboardingRecord
        : this.startOnboardingByDefault;
      if (this.employee) {
        this.form = {
          ...this.employee,
          serviceGroupIds: this.employee.serviceGroupIds?.length
            ? [...this.employee.serviceGroupIds]
            : (this.employee.serviceGroupId ? [this.employee.serviceGroupId] : []),
          joiningDate: this.employee.joiningDate ? this.employee.joiningDate.substring(0, 10) : undefined
        };
      } else {
        this.form = this.getEmpty();
      }
      this.joiningDatePicker = this.toPickerDate(this.form.joiningDate);
      this.inactiveDatePicker = this.toPickerDate(this.form.inactiveDate);
      this.fieldTouched = {};
      this.cvFile = null;
      this.cvUploading = false;
      if (!this.optionsLoaded) {
        this.loadOptions();
      }
    }
  }

  loadOptions(): void {
    this.loadingOptions = true;
    forkJoin({
      roles: this.roleService.getAllRoles().pipe(catchError(() => of([]))),
      designations: this.designationService.getAllDesignations().pipe(catchError(() => of([]))),
      categories: this.categoryService.getAllCategories().pipe(catchError(() => of([]))),
      serviceGroups: this.serviceGroupService.getAllServiceGroups().pipe(catchError(() => of([]))),
      employees: this.employeeService.getEmployeeNames().pipe(catchError(() => of([])))
    }).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (data) => {
        this.roleOptions = (data.roles as any[]).filter(r => r.isActive).map((r: any) => ({ label: r.title, value: r.id }));
        this.designationOptions = (data.designations as any[]).filter(d => d.isActive).map((d: any) => ({ label: d.name, value: d.id }));
        this.categoryOptions = (data.categories as any[]).filter(c => c.isActive).map((c: any) => ({ label: c.name, value: c.id }));
        this.serviceGroupOptions = (data.serviceGroups as any[]).filter(sg => sg.isActive).map((sg: any) => ({ label: sg.name, value: sg.id }));
        this.headOfDepartmentOptions = (data.employees as { id: string; name: string }[]).map(e => ({ label: e.name, value: e.id }));
        this.loadingOptions = false;
        this.optionsLoaded = true;
      }
    });
  }

  onCvFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.files?.length) return;
    const file = input.files[0];
    const allowed = ['application/pdf', 'application/msword', 'application/vnd.openxmlformats-officedocument.wordprocessingml.document'];
    if (!allowed.includes(file.type)) {
      this.messageService.add({ severity: 'warn', summary: 'Invalid File', detail: 'Only PDF, DOC, DOCX files are allowed.' });
      input.value = '';
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      this.messageService.add({ severity: 'warn', summary: 'File Too Large', detail: 'Max file size is 10 MB.' });
      input.value = '';
      return;
    }
    this.cvFile = file;
    this.cvUploading = true;
    this.employeeService.uploadCv(file).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
      next: (res) => {
        this.form.cvAttachmentUrl = res.url;
        this.cvUploading = false;
        this.messageService.add({ severity: 'success', summary: 'Uploaded', detail: `${res.fileName} uploaded` });
      },
      error: (err) => {
        this.cvUploading = false;
        this.cvFile = null;
        input.value = '';
        this.messageService.add({ severity: 'error', summary: 'Upload Failed', detail: err.error?.message || 'Failed to upload file' });
      }
    });
  }

  removeCv(): void {
    this.form.cvAttachmentUrl = undefined;
    this.cvFile = null;
  }

  get cvFileName(): string {
    if (this.cvFile) return this.cvFile.name;
    if (this.form.cvAttachmentUrl) {
      const parts = this.form.cvAttachmentUrl.split('/');
      return parts[parts.length - 1];
    }
    return '';
  }

  isValidEmail(email: string): boolean {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  }

  save(): void {
    this.fieldTouched['name'] = true;
    this.fieldTouched['roleIds'] = true;
    this.fieldTouched['serviceGroupIds'] = true;
    this.fieldTouched['email'] = true;
    this.fieldTouched['personalEmail'] = true;

    const roleIds = this.form.roleIds?.length ? this.form.roleIds : (this.form.roleId ? [this.form.roleId] : []);
    if (!roleIds.length || !this.form.name?.trim()) return;
    const serviceGroupIds = this.form.serviceGroupIds?.length ? this.form.serviceGroupIds : (this.form.serviceGroupId ? [this.form.serviceGroupId] : []);
    if (!serviceGroupIds.length) return;
    if (this.form.email && !this.isValidEmail(this.form.email)) return;
    if (this.form.personalEmail && !this.isValidEmail(this.form.personalEmail)) return;

    // Fold the pickers' Dates back into the strings the API expects. An active employee
    // never carries an inactive date, whichever way the picker was left.
    this.form.joiningDate = this.fromPickerDate(this.joiningDatePicker);
    this.form.inactiveDate = this.form.isActive ? undefined : this.fromPickerDate(this.inactiveDatePicker);

    this.saving = true;
    if (this.isEditMode) {
      const toSave = { ...this.form };
      if (toSave.roleIds?.length) toSave.roleId = toSave.roleIds[0];
      if (toSave.serviceGroupIds?.length) toSave.serviceGroupId = toSave.serviceGroupIds[0];
      this.employeeService.updateEmployee(this.form.id, toSave).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
        next: (result) => {
          const finishEdit = (startedNow: boolean) => {
            this.saving = false;
            this.messageService.add({ severity: 'success', summary: 'Saved', detail: 'Employee updated successfully' });
            this.saved.emit({ employee: result, isNew: false, startOnboarding: startedNow });
            this.closeDialog();
          };

          // Ticked on an employee that had no record: start onboarding now. This is CREATE-only —
          // an existing record leaves the checkbox disabled, so nothing here can delete tasks that
          // may already be part-complete.
          if (this.startOnboarding && !this.hasOnboardingRecord) {
            this.onboardingService.createRecord({ employeeId: result.id })
              .pipe(catchError(() => of(null)), takeUntilDestroyed(this.destroyRef))
              .subscribe(() => finishEdit(true));
          } else {
            finishEdit(false);
          }
        },
        error: (err) => {
          this.saving = false;
          this.messageService.add({ severity: 'error', summary: 'Error', detail: err.error?.details || err.error?.message || 'Failed to update employee' });
        }
      });
    } else {
      this.employeeService.addEmployee(this.form).pipe(takeUntilDestroyed(this.destroyRef)).subscribe({
        next: (result) => {
          const finish = () => {
            this.saving = false;
            this.messageService.add({ severity: 'success', summary: 'Saved', detail: `${result.name} added successfully` });
            this.saved.emit({ employee: result, isNew: true, startOnboarding: this.startOnboarding });
            this.closeDialog();
          };

          // Onboarding is created here only when the user asked for it. This used to run
          // unconditionally, which is why unticking the checkbox still produced a checklist.
          // (Forecast is auto-created by the backend and is unaffected.)
          if (this.startOnboarding) {
            this.onboardingService.createRecord({ employeeId: result.id })
              .pipe(catchError(() => of(null)), takeUntilDestroyed(this.destroyRef))
              .subscribe(() => finish());
          } else {
            finish();
          }
        },
        error: (err) => {
          this.saving = false;
          this.messageService.add({ severity: 'error', summary: 'Error', detail: err.error?.details || err.error?.message || 'Failed to add employee' });
        }
      });
    }
  }

  reset(): void {
    if (this.isEditMode && this.employee) {
      this.form = {
        ...this.employee,
        serviceGroupIds: this.employee.serviceGroupIds?.length
          ? [...this.employee.serviceGroupIds]
          : (this.employee.serviceGroupId ? [this.employee.serviceGroupId] : []),
        joiningDate: this.employee.joiningDate ? this.employee.joiningDate.substring(0, 10) : undefined
      };
    } else {
      this.form = this.getEmpty();
    }
    this.joiningDatePicker = this.toPickerDate(this.form.joiningDate);
    this.inactiveDatePicker = this.toPickerDate(this.form.inactiveDate);
    this.fieldTouched = {};
    this.cvFile = null;
  }

  closeDialog(): void {
    this.visibleChange.emit(false);
    this.cancel.emit();
  }

  private getEmpty(): Employee {
    return {
      id: '', name: '', email: '', roleId: '', roleIds: [],
      serviceGroupId: '', serviceGroupIds: [],
      isActive: true, excludeFromAllocation: false,
      excludeFromForecast: false, excludeFromTimesheet: false,
      excludeFromFutureAllocation: false,
      joiningDate: new Date().toISOString().substring(0, 10)
    };
  }

  /**
   * Flips Active/Inactive and keeps the date in step. Deactivating pre-fills today, since an
   * admin flipping the switch is normally acting now — but the field stays editable so a
   * leaver being recorded late can be backdated to their real last working day. Reactivating
   * clears the date, so an active employee never carries a leaving date.
   */
  toggleActive(): void {
    this.form.isActive = !this.form.isActive;
    if (this.form.isActive) {
      this.inactiveDatePicker = null;
    } else if (!this.inactiveDatePicker) {
      this.inactiveDatePicker = new Date();
    }
  }

  /** 'YYYY-MM-DD' -> Date for the picker. Parsed as local time, not UTC, so the
   *  displayed day cannot shift by one in negative-offset timezones. */
  private toPickerDate(value?: string): Date | null {
    if (!value) return null;
    const [y, m, d] = value.substring(0, 10).split('-').map(Number);
    if (!y || !m || !d) return null;
    return new Date(y, m - 1, d);
  }

  /** Date -> 'YYYY-MM-DD' using local parts (toISOString would shift the day). */
  private fromPickerDate(value: Date | null): string | undefined {
    if (!value) return undefined;
    const pad = (n: number) => String(n).padStart(2, '0');
    return `${value.getFullYear()}-${pad(value.getMonth() + 1)}-${pad(value.getDate())}`;
  }
}
